import { Link } from "react-router-dom";
import { useState } from "react";
import { BookOpen, ShieldCheck, Truck, RotateCcw, HeadphonesIcon, Facebook, Instagram, Twitter, Youtube, CreditCard, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const PAYMENT_ICONS = ["visa", "mastercard", "amex", "discover", "apple-pay", "google-pay"];

function PaymentBadges() {
  return (
    <div className="flex items-center gap-2 flex-wrap" aria-label="Payment methods">
      {PAYMENT_ICONS.map((p) => (
        <span key={p} className="inline-flex items-center justify-center h-7 min-w-[42px] px-2 rounded-md bg-white border border-slate-200 text-[10px] font-bold uppercase text-slate-700">
          {p === "apple-pay" ? "" : p === "google-pay" ? "GPay" : p.slice(0, 4)}
        </span>
      ))}
    </div>
  );
}

export default function Footer() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list. Welcome!");
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Subscription failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="mt-24 bg-secondary text-white" data-testid="site-footer">
      {/* Newsletter band */}
      <div className="bg-primary/95">
        <div className="container-book py-10 grid gap-6 md:grid-cols-[1fr,auto] items-center">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white">Get exclusive discounts and new arrivals</h3>
            <p className="text-white/85 mt-1 text-sm">Join thousands of American students and educators saving on every order.</p>
          </div>
          <form onSubmit={subscribe} className="flex gap-2 w-full md:w-auto md:min-w-[420px]" data-testid="newsletter-form">
            <Input
              type="email"
              required
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 bg-white text-slate-900 rounded-xl border-0 focus-visible:ring-2 focus-visible:ring-accent"
              data-testid="newsletter-email"
            />
            <Button
              type="submit"
              disabled={loading}
              className="h-12 rounded-xl bg-accent text-secondary hover:bg-accent/90 font-semibold px-6"
              data-testid="newsletter-submit"
            >
              {loading ? "Joining…" : "Subscribe"}
            </Button>
          </form>
        </div>
      </div>

      {/* Main grid */}
      <div className="container-book py-14 grid gap-10 md:grid-cols-4">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
              <BookOpen className="text-secondary" size={20} strokeWidth={2.4} />
            </div>
            <div>
              <div className="font-heading font-semibold text-lg">Blessings Connection</div>
              <div className="text-[10px] font-medium uppercase tracking-[0.18em] text-accent -mt-0.5">LLC</div>
            </div>
          </div>
          <p className="text-sm text-white/75 leading-relaxed">
            Your trusted U.S. bookstore for new textbooks, academic, professional, and educational titles.
            Thousands of titles, verified authenticity, fast delivery.
          </p>
          <div className="mt-6 flex gap-3">
            <a href="#" aria-label="Facebook" className="w-9 h-9 rounded-lg bg-white/10 hover:bg-accent hover:text-secondary flex items-center justify-center transition-colors"><Facebook size={16} /></a>
            <a href="#" aria-label="Instagram" className="w-9 h-9 rounded-lg bg-white/10 hover:bg-accent hover:text-secondary flex items-center justify-center transition-colors"><Instagram size={16} /></a>
            <a href="#" aria-label="Twitter" className="w-9 h-9 rounded-lg bg-white/10 hover:bg-accent hover:text-secondary flex items-center justify-center transition-colors"><Twitter size={16} /></a>
            <a href="#" aria-label="Youtube" className="w-9 h-9 rounded-lg bg-white/10 hover:bg-accent hover:text-secondary flex items-center justify-center transition-colors"><Youtube size={16} /></a>
          </div>
        </div>

        <div>
          <h4 className="font-heading font-semibold text-white mb-4">Company</h4>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link to="/about" className="hover:text-accent transition-colors">About us</Link></li>
            <li><Link to="/contact" className="hover:text-accent transition-colors flex items-center gap-1"><Mail size={12} /> Contact us</Link></li>
            <li>{user ? <Link to="/account" className="hover:text-accent transition-colors" data-testid="footer-account-link">My account</Link> : <Link to="/login" className="hover:text-accent transition-colors" data-testid="footer-signin-link">Sign in</Link>}</li>
          </ul>
        </div>

        <div>
          <h4 className="font-heading font-semibold text-white mb-4">Customer service</h4>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link to="/faq" className="hover:text-accent transition-colors">FAQ</Link></li>
            <li><Link to="/shipping" className="hover:text-accent transition-colors">Shipping &amp; cancellations</Link></li>
            <li><Link to="/returns" className="hover:text-accent transition-colors">Return &amp; refund policy</Link></li>
            <li><Link to="/privacy" className="hover:text-accent transition-colors">Privacy policy</Link></li>
            <li><Link to="/terms" className="hover:text-accent transition-colors">Terms &amp; conditions</Link></li>
            <li><Link to="/account" className="hover:text-accent transition-colors">Track your order</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-heading font-semibold text-white mb-4">We accept</h4>
          <PaymentBadges />
          <div className="mt-6 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-white/85"><ShieldCheck size={14} className="text-accent" /> SSL Secure Checkout</div>
            <div className="flex items-center gap-2 text-white/85"><Truck size={14} className="text-accent" /> Fast U.S. shipping</div>
            <div className="flex items-center gap-2 text-white/85"><RotateCcw size={14} className="text-accent" /> 15-day easy returns</div>
            <div className="flex items-center gap-2 text-white/85"><HeadphonesIcon size={14} className="text-accent" /> USA customer support</div>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/10">
        <div className="container-book py-5 text-xs text-white/60 flex flex-col md:flex-row md:items-center gap-3 justify-between">
          <div>© {new Date().getFullYear()} Blessings Connection LLC. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <span>Made with care in the USA</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
